import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { ScrollArea } from "../../../ui/scroll-area";
import {
  Lightbulb,
  Calendar,
  Tag,
  ChevronLeft,
  ChevronRight,
  Star,
  ExternalLink,
  Edit2,
  CheckSquare,
  Copy,
  Check,
} from "lucide-react";
import { UserTakeaway } from "../../../../api/userTakeaways";
import { cn } from "../../../../lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";

interface InsightViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  insight: UserTakeaway | null;
  insights?: UserTakeaway[]; // Optional: for navigation between multiple insights
  themeName?: string; // Optional: theme name if viewing from synthesis
  onEdit?: (insight: UserTakeaway) => void;
  onConvertToAction?: (insight: UserTakeaway) => void;
  onViewEngagement?: (engagementId: string) => void;
  onToggleFavorite?: (id: string) => void;
  isFavorite?: boolean;
  mode?: "dialog" | "sheet"; // Display mode
  onNavigate?: (insight: UserTakeaway) => void; // Callback when navigating between insights
}

const InsightViewerDialog: React.FC<InsightViewerDialogProps> = ({
  open,
  onOpenChange,
  insight,
  insights = [],
  themeName,
  onEdit,
  onConvertToAction,
  onViewEngagement,
  onToggleFavorite,
  isFavorite = false,
  mode = "sheet",
  onNavigate,
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  // Find current insight index if insights array provided
  const effectiveIndex = insights.length > 0
    ? insights.findIndex((i) => i._id === insight?._id)
    : -1;

  const displayIndex = effectiveIndex >= 0 ? effectiveIndex : currentIndex;
  const currentInsight = insights.length > 0 ? insights[displayIndex] : insight;

  const canNavigate = insights.length > 1;
  const hasPrev = displayIndex > 0;
  const hasNext = displayIndex < insights.length - 1;

  const handlePrev = () => {
    if (hasPrev) {
      const newIndex = displayIndex - 1;
      setCurrentIndex(newIndex);
      const newInsight = insights[newIndex];
      if (newInsight && onNavigate) {
        onNavigate(newInsight);
      }
    }
  };

  const handleNext = () => {
    if (hasNext) {
      const newIndex = displayIndex + 1;
      setCurrentIndex(newIndex);
      const newInsight = insights[newIndex];
      if (newInsight && onNavigate) {
        onNavigate(newInsight);
      }
    }
  };

  const handleCopy = async () => {
    if (currentInsight?.content) {
      await navigator.clipboard.writeText(currentInsight.content);
      setCopied(true);
      toast.success(t("copied_to_clipboard") || "Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (date: string | Date | number) => {
    return format(new Date(date), "MMM d, yyyy 'at' h:mm a");
  };

  const getSourceLabel = (source?: UserTakeaway["source"]) => {
    if (!source?.type) return t("unknown_source") || "Unknown";
    switch (source.type) {
      case "engagement":
        return t("from_engagement") || "From Engagement";
      case "user_created":
        return t("manually_added") || "Manually Added";
      case "mentor_assigned":
        return t("mentor_assigned") || "Mentor Assigned";
      case "sketchbook":
        return t("from_sketchbook") || "From Sketchbook";
      case "reflection":
        return t("source_reflection") || "Daily reflection";
      default:
        return source.type;
    }
  };

  if (!currentInsight) return null;

  const content = (
    <div className="flex flex-col h-full">
      {/* Header with navigation */}
      {themeName && (
        <div className="mb-4 pb-3 border-b">
          <div className="flex items-center gap-2 text-sm text-violet-600">
            <Tag className="h-4 w-4" />
            <span className="font-medium">{themeName}</span>
            {canNavigate && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {displayIndex + 1} / {insights.length}
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Main content */}
      <ScrollArea className="flex-1 -mx-2 px-2">
        <div className="space-y-4">
          {/* Insight content */}
          <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Lightbulb className="h-5 w-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                {currentInsight.source?.type === "reflection" && currentInsight.source.question && (
                  <p className="text-sm text-amber-700/70 italic mb-1.5">
                    {currentInsight.source.question}
                  </p>
                )}
                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                  {currentInsight.content}
                </p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="space-y-3">
            {/* Category */}
            {currentInsight.category && (
              <div className="flex items-center gap-2 text-sm">
                <Tag className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t("category")}:</span>
                <Badge
                  variant="outline"
                  className="flex items-center gap-1.5"
                  style={{
                    borderColor: currentInsight.category.color,
                    backgroundColor: `${currentInsight.category.color}10`,
                  }}
                >
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: currentInsight.category.color }}
                  />
                  {currentInsight.category.name}
                </Badge>
              </div>
            )}

            {/* Source */}
            <div className="flex items-center gap-2 text-sm">
              <ExternalLink className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t("source")}:</span>
              <span className="text-gray-700">{getSourceLabel(currentInsight.source)}</span>
              {currentInsight.source?.type === "engagement" && currentInsight.source.fiProcess && onViewEngagement && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-violet-600"
                  onClick={() => onViewEngagement(currentInsight.source!.fiProcess!)}
                >
                  {t("view_engagement") || "View"}
                </Button>
              )}
            </div>

            {/* Created date */}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t("created")}:</span>
              <span className="text-gray-700">{formatDate(currentInsight.createdOn)}</span>
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Actions footer */}
      <div className="mt-4 pt-4 border-t space-y-3">
        {/* Navigation */}
        {canNavigate && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrev}
              disabled={!hasPrev}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              {t("previous") || "Previous"}
            </Button>
            <span className="text-xs text-muted-foreground">
              {displayIndex + 1} of {insights.length} insights
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNext}
              disabled={!hasNext}
            >
              {t("next") || "Next"}
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {onToggleFavorite && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleFavorite(currentInsight._id)}
              className={cn(
                isFavorite && "text-amber-500 border-amber-200 bg-amber-50"
              )}
            >
              <Star className={cn("h-4 w-4 mr-1", isFavorite && "fill-amber-500")} />
              {isFavorite ? t("favorited") || "Favorited" : t("favorite") || "Favorite"}
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
          >
            {copied ? (
              <Check className="h-4 w-4 mr-1 text-green-500" />
            ) : (
              <Copy className="h-4 w-4 mr-1" />
            )}
            {t("copy") || "Copy"}
          </Button>

          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onEdit(currentInsight);
                onOpenChange(false);
              }}
            >
              <Edit2 className="h-4 w-4 mr-1" />
              {t("edit") || "Edit"}
            </Button>
          )}

          {onConvertToAction && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onConvertToAction(currentInsight);
                onOpenChange(false);
              }}
              className="text-primary border-primary/20 hover:bg-primary/5"
            >
              <CheckSquare className="h-4 w-4 mr-1" />
              {t("convert_to_action") || "To Action"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  if (mode === "dialog") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px] max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              {t("view_insight") || "View Insight"}
            </DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[450px] flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            {t("view_insight") || "View Insight"}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 mt-4 overflow-hidden">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InsightViewerDialog;
